// Settings → Users → a user's access: one row per sidebar menu item (the same menu definition the sidebar uses), each
// No access / View / Full. Group rows set all their items (showing "Mixed" when they differ); "Set all to …" buttons;
// "Start from" (a copy of a starting point) and "Copy access from another user"; three tick boxes (costs and prices,
// delete records, fibre composition) and the records a supplier or agent user sees. Saved with the server, which checks.
import { COMPOSITION_PERMISSION_HELP } from "../../../shared/composition.js";
import { badge, esc, get, put, toast } from "../lib.js";
import { navigate, onAction, onChange, route } from "../state.js";
import { backBar, errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import { accessRows } from "../../../shared/menu.js";
import { ACTION_LABEL, LEVEL_LABEL, LEVELS, STARTING_POINTS, groupLevel, rowLevel, startingPoint, type Access, type Level } from "../../../shared/access.js";

let draft: Access | null = null;
let ctx: { id: string; member: any; dataScope: any; canEdit: boolean; canGrantSettings: boolean; users: any[]; scope: any } | null = null;

route("/app/users/:id/access", async ({ params }) => {
  renderShell({ active: "settings", title: "User access", subtitle: "", content: loadingPanel() });
  try {
    const [d, list, scope] = await Promise.all([get(`/api/org/users/${params.id}/access`), get("/api/org/users"), get("/api/org/users/scope-options").catch(() => ({ suppliers: [], agents: [], pos: [] }))]);
    draft = structuredClone(d.access);
    ctx = { id: params.id, member: d.member, dataScope: d.dataScope, canEdit: d.canEdit, canGrantSettings: d.canGrantSettings, users: list.users.filter((u: any) => u.membershipId !== params.id && u.status !== "deleted"), scope };
    draw();
  } catch (err) { setContent(errorPanel((err as Error).message)); }
});

const choice = (name: string, value: Level | "mixed", disabled: (l: Level) => boolean, label: string) =>
  `<div class="acc-choice" role="radiogroup" aria-label="${esc(label)}">${LEVELS.map((l) => `<label class="acc-opt acc-${l} ${value === l ? "on" : ""} ${disabled(l) ? "off" : ""}"><input type="radio" name="${esc(name)}" value="${l}" ${value === l ? "checked" : ""} ${disabled(l) ? "disabled" : ""} data-change="acc-set"><span>${LEVEL_LABEL[l]}</span></label>`).join("")}${value === "mixed" ? '<span class="acc-mixed">Mixed</span>' : ""}</div>`;

function rowsHtml(): string {
  const a = draft!, edit = ctx!.canEdit;
  const lock = (row: string) => (l: Level) => !edit || (row === "settings" && l === "full" && !ctx!.canGrantSettings && rowLevel(a, "settings") !== "full");
  const rows = accessRows();
  let html = "", group = "";
  for (const r of rows) {
    if (r.group && r.group !== group) {
      group = r.group;
      html += `<div class="acc-row acc-group"><span class="acc-label">${esc(r.groupLabel)}</span>${choice(`g:${r.group}`, groupLevel(a, r.group), (l) => !edit || (l === "full" && r.group === "settings"), r.groupLabel)}</div>`;
    } else if (!r.group) group = "";
    const ex = a.except[r.id];
    html += `<div class="acc-row ${r.group ? "acc-child" : ""}" data-row="${esc(r.id)}"><span class="acc-label">${esc(r.label)}${r.also ? `<small class="muted"> · includes ${esc(r.also.join(", "))}</small>` : ""}${r.hint ? `<small class="muted"> · ${esc(r.hint)}</small>` : ""}${a.limited?.[r.id] && rowLevel(a, r.id) === "full" ? `<small class="acc-except">Full only for what they had: no general editing (carried over)</small>` : ""}${ex?.length && rowLevel(a, r.id) === "full" ? `<small class="acc-except">Full without ${esc(ex.map((x) => ACTION_LABEL[x] || x).join(", "))} (carried over)${edit ? ` <button type="button" class="link-btn" data-action="acc-clear-except" data-row="${esc(r.id)}">Allow</button>` : ""}</small>` : ""}</span>${choice(`r:${r.id}`, rowLevel(a, r.id), lock(r.id), r.label)}</div>`;
  }
  return html;
}

function draw() {
  if (!draft || !ctx) return;
  const m = ctx.member, a = draft, edit = ctx.canEdit, s = ctx.dataScope;
  const g = a.except["*"];
  renderShell({ active: "settings", title: `Access · ${m.userCode} · ${m.firstName} ${m.lastName}`, subtitle: "What this user can open and do. The role is only a label.", content: "" });
  setContent(`${backBar("/app/users", "Back to Users")}
<div class="panel acc-panel">
<div class="acc-head"><div><strong>${esc(m.userCode)}</strong> · ${esc(`${m.firstName} ${m.lastName}`)} ${badge(m.role, "gray")}</div>
${edit ? `<div class="acc-tools">
<label class="acc-tool">Start from <select data-change="acc-start" aria-label="Start from"><option value="">Choose…</option>${STARTING_POINTS.map(([k, l]) => `<option value="${k}">${esc(l)}</option>`).join("")}</select></label>
<label class="acc-tool">Copy access from <select data-change="acc-copy" aria-label="Copy access from another user"><option value="">Another user…</option>${ctx.users.map((u: any) => `<option value="${esc(u.membershipId)}">${esc(`${u.userCode} · ${u.firstName} ${u.lastName}`)}</option>`).join("")}</select></label></div>` : '<p class="muted">You can see this user\'s access but not change it.</p>'}</div>
${edit ? `<div class="acc-setall"><button type="button" class="secondary small-btn" data-action="acc-all" data-v="none">Set all to No access</button><button type="button" class="secondary small-btn" data-action="acc-all" data-v="view">Set all to View</button><button type="button" class="secondary small-btn" data-action="acc-all" data-v="full">Set all to Full</button></div>` : ""}
<p class="muted acc-key"><strong>No access</strong>: not in their menu. <strong>View</strong>: open and read, export. <strong>Full</strong>: create, edit, approve and submit. Help is always there.</p>
<div class="acc-rows" id="acc-rows">${rowsHtml()}</div>
<div class="acc-ticks">
<label class="check-row"><input type="checkbox" data-change="acc-tick" data-k="costs" ${a.costs ? "checked" : ""} ${edit ? "" : "disabled"}><span>Can see costs and prices <small class="muted">FOB prices, costings, margins and finance amounts (Finance needs this)</small></span></label>
<label class="check-row"><input type="checkbox" data-change="acc-tick" data-k="delete" ${a.delete ? "checked" : ""} ${edit ? "" : "disabled"}><span>Can delete records <small class="muted">on the items set to Full</small></span></label>
<label class="check-row"><input type="checkbox" data-change="acc-tick" data-k="composition" ${a.composition ? "checked" : ""} ${edit ? "" : "disabled"}><span>Can see fibre composition <small class="muted">${esc(COMPOSITION_PERMISSION_HELP)}</small></span></label>
${g?.length ? `<p class="acc-except">Everywhere without ${esc(g.map((x) => ACTION_LABEL[x] || x).join(", "))} (carried over)${edit ? ' <button type="button" class="link-btn" data-action="acc-clear-except" data-row="*">Allow</button>' : ""}</p>` : ""}
</div>
${scopeBox()}
${edit ? '<div class="form-actions acc-actions"><a class="secondary" href="/app/users" data-link>Cancel</a><button type="button" class="primary" data-action="acc-save">Save access</button></div>' : ""}
</div>`);
}
// ---- Which orders this user can see ----------------------------------------------------------------------------------
const SCOPE_OPTIONS: Array<[string, string, string]> = [
  ["all", "All records", "Every order in the organisation. For your own staff."],
  ["supplier", "Linked supplier only", "Only orders placed with this user's supplier."],
  ["agent", "Linked agent only", "Only orders handled by this user's agent."],
  ["selectedPOs", "Selected POs only", "Only the purchase orders you choose."],
];
let poFilter = "";
function scopeBox(): string {
  const s = ctx!.dataScope, edit = ctx!.canEdit, opt = SCOPE_OPTIONS.find(([k]) => k === s.type) || SCOPE_OPTIONS[0];
  const linked = (kind: "supplier" | "agent") => {
    const list = kind === "supplier" ? ctx!.scope.suppliers : ctx!.scope.agents, key = kind === "supplier" ? "supplierUid" : "agentUid";
    const cur = list.find((x: any) => x.id === s[key]);
    return `<div class="acc-scope-link"><span class="acc-scope-who">${cur ? `Linked to <strong>${esc(cur.name)}</strong>` : `<span class="acc-scope-none">Not linked to ${kind === "supplier" ? "a supplier" : "an agent"} yet</span>`}</span>
<label>${kind === "supplier" ? "Supplier" : "Agent"} <select data-change="acc-scope-id" data-k="${key}" ${edit ? "" : "disabled"} aria-label="${kind === "supplier" ? "Linked supplier" : "Linked agent"}"><option value="">Choose…</option>${list.map((x: any) => `<option value="${esc(x.id)}" ${x.id === s[key] ? "selected" : ""}>${esc(x.name)}</option>`).join("")}</select></label></div>`;
  };
  const pos = () => {
    const chosen = new Set<string>(s.poUids || []);
    return `<div class="acc-pos-head"><input type="search" class="acc-pos-search" data-change="acc-po-search" placeholder="Search purchase orders" aria-label="Search purchase orders" value="${esc(poFilter)}" ${edit ? "" : "disabled"}><span class="acc-pos-count" id="acc-pos-count">${chosen.size} selected</span></div>
<div class="acc-pos" id="acc-pos">${ctx!.scope.pos.map((x: any) => `<label class="check-row" data-name="${esc(String(x.name).toLowerCase())}" ${poFilter && !String(x.name).toLowerCase().includes(poFilter.toLowerCase()) ? "hidden" : ""}><input type="checkbox" data-change="acc-scope-po" value="${esc(x.id)}" ${chosen.has(x.id) ? "checked" : ""} ${edit ? "" : "disabled"}><span>${esc(x.name)}${x.status === "cancelled" ? ' <small class="muted">cancelled</small>' : ""}</span></label>`).join("") || '<span class="muted">No purchase orders yet.</span>'}</div>`;
  };
  return `<section class="acc-scope-box" aria-labelledby="acc-scope-h"><h4 id="acc-scope-h">Which orders this user can see</h4>
<div class="form-message acc-scope-msg" id="acc-scope-msg" role="alert" hidden></div>
<select data-change="acc-scope" aria-label="Which orders this user can see" ${edit ? "" : "disabled"}>${SCOPE_OPTIONS.map(([k, l]) => `<option value="${k}" ${s.type === k ? "selected" : ""}>${l}</option>`).join("")}</select>
<small class="muted acc-scope-help">${esc(opt[2])}</small>
${s.type === "supplier" ? linked("supplier") : s.type === "agent" ? linked("agent") : s.type === "selectedPOs" ? pos() : ""}
</section>`;
}
/** A red box in the order limit when a link or a PO is missing (and the save is stopped). */
function scopeProblem(): string {
  const s = ctx!.dataScope;
  if (s.type === "supplier" && !s.supplierUid) return "Choose the supplier this user is linked to.";
  if (s.type === "agent" && !s.agentUid) return "Choose the agent this user is linked to.";
  if (s.type === "selectedPOs" && !(s.poUids || []).length) return "Tick at least one purchase order this user can see.";
  return "";
}
function showScopeProblem(text: string) {
  const box = document.getElementById("acc-scope-msg");
  document.querySelector(".acc-scope-box")?.classList.toggle("is-error", !!text);
  if (!box) return;
  box.textContent = text;
  box.hidden = !text;
  if (text) box.scrollIntoView({ behavior: "smooth", block: "center" });
}
/** The PO search filters the tick list as you type (ticked POs stay ticked when hidden). */
function filterPos(text: string) {
  poFilter = text.trim();
  for (const l of document.querySelectorAll<HTMLElement>("#acc-pos label[data-name]")) l.hidden = !!poFilter && !l.dataset.name!.includes(poFilter.toLowerCase());
}
onChange("acc-po-search", (el) => filterPos((el as HTMLInputElement).value));
document.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement;
  if (t.classList?.contains("acc-pos-search")) filterPos(t.value);
});
const redrawRows = () => { const el = document.getElementById("acc-rows"); if (el) el.innerHTML = rowsHtml(); };

onChange("acc-set", (el) => {
  if (!draft) return;
  const input = el as HTMLInputElement, [kind, id] = input.name.split(":"), v = input.value as Level;
  const ids = kind === "g" ? accessRows().filter((r) => r.group === id).map((r) => r.id) : [id];
  for (const r of ids) {
    if (r === "settings" && v === "full" && !ctx!.canGrantSettings && rowLevel(draft, "settings") !== "full") continue;
    draft.items[r] = v;
    delete draft.except[r];   // choosing a level gives that level in full
    if (draft.limited) delete draft.limited[r];
  }
  redrawRows();
});
onAction("acc-all", (el) => {
  if (!draft) return;
  const v = el.dataset.v as Level;
  for (const r of accessRows()) {
    if (r.id === "settings" && v === "full" && !ctx!.canGrantSettings && rowLevel(draft, "settings") !== "full") continue;
    draft.items[r.id] = v;
  }
  draft.except = {};
  draft.limited = undefined;
  redrawRows();
});
onAction("acc-clear-except", (el) => { if (draft) { delete draft.except[el.dataset.row!]; if (draft.limited) delete draft.limited[el.dataset.row!]; draw(); } });
onChange("acc-tick", (el) => { if (draft) (draft as any)[el.dataset.k!] = (el as HTMLInputElement).checked; });
onChange("acc-start", (el) => {
  const v = (el as HTMLSelectElement).value;
  if (!v || !draft) return;
  const next = startingPoint(v);
  if (!ctx!.canGrantSettings && next.items.settings === "full" && rowLevel(draft, "settings") !== "full") next.items.settings = rowLevel(draft, "settings");
  draft = next;
  draw();
  toast(`Filled in from "${STARTING_POINTS.find(([k]) => k === v)?.[1]}". Change any row, then Save access.`);
});
onChange("acc-copy", async (el) => {
  const v = (el as HTMLSelectElement).value;
  if (!v || !draft) return;
  try {
    const d = await get(`/api/org/users/${v}/access`);
    draft = structuredClone(d.access);
    if (!ctx!.canGrantSettings && draft!.items.settings === "full") draft!.items.settings = "view";
    draw();
    toast(`Copied from ${d.member.firstName} ${d.member.lastName}. Change any row, then Save access.`);
  } catch (err) { toast((err as Error).message, "error"); }
});
onChange("acc-scope", (el) => { if (ctx) { ctx.dataScope = { ...ctx.dataScope, type: (el as HTMLSelectElement).value }; redrawScope(); } });
onChange("acc-scope-id", (el) => { if (ctx) { ctx.dataScope = { ...ctx.dataScope, [el.dataset.k!]: (el as HTMLSelectElement).value }; redrawScope(); } });
/** Only the order-limit box is redrawn (the permission rows keep their unsaved changes). */
function redrawScope() {
  const box = document.querySelector(".acc-scope-box");
  if (box) box.outerHTML = scopeBox();
}
onChange("acc-scope-po", (el) => {
  if (!ctx) return;
  const id = (el as HTMLInputElement).value, on = (el as HTMLInputElement).checked;
  const set = new Set<string>(ctx.dataScope.poUids || []);
  if (on) set.add(id); else set.delete(id);
  ctx.dataScope = { ...ctx.dataScope, poUids: [...set] };
  const n = document.getElementById("acc-pos-count");
  if (n) n.textContent = `${set.size} selected`;
  if (set.size) showScopeProblem("");
});
onAction("acc-save", async (el) => {
  if (!draft || !ctx) return;
  const s = ctx.dataScope;
  const problem = scopeProblem();
  if (problem) return showScopeProblem(problem);
  (el as HTMLButtonElement).disabled = true;
  try {
    await put(`/api/org/users/${ctx.id}/access`, { access: draft, dataScope: { type: s.type, supplierUid: s.type === "supplier" ? s.supplierUid : "", agentUid: s.type === "agent" ? s.agentUid : "", poUids: s.type === "selectedPOs" ? s.poUids : [] } });
    toast("Access saved.");
    void navigate("/app/users");
  } catch (err) {
    const msg = (err as Error).message;
    if (/supplier this user|agent this user|purchase order this user/.test(msg)) showScopeProblem(msg); else toast(msg, "error");
    (el as HTMLButtonElement).disabled = false;
  }
});
